import { memo, useState } from 'react'

import { CopyAll, KeyboardArrowDown } from '@mui/icons-material'
import { Box, Dialog, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Typography } from '@mui/material'
import { AvcInstanceSdiAudioChannelUtilization, AvcInstanceSdiThumbnail } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'

import { SdiImage, SdiPanelDialogContent } from 'domains/ContentProcessing/AvcInstances/Instance/Dashboard/Sdi'
import { ThemeTypes } from 'stores/settings'
import { getIsActiveColor } from 'utils'

export type SdiDevice = {
  isActive: boolean
  videoFormat?: string
  volume?: number
  thumbnail?: AvcInstanceSdiThumbnail
  id: string
  name: string
  deviceId: string
  model: string
  width?: number
  height?: number
  fieldOrder?: string
  frameRate?: string | number
  sampleRate?: number
  channels?: AvcInstanceSdiAudioChannelUtilization[]
}

const SdiPanelRaw = ({ id, thumbnail, name, videoFormat, isActive, deviceId, volume, ...sdi }: SdiDevice) => {
  const { t } = useTranslation(['common', 'domain'])

  const { enqueueSnackbar } = useSnackbar()

  const [dialog, setDialog] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>()

  return (
    <>
      <Paper
        sx={[
          {
            p: 4,
            pb: 3,
            minWidth: 200,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          },
          isActive
            ? {
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: (theme) => (isActive ? theme.palette.background.content : 'auto'),
                },
              }
            : null,
        ]}
        key={id}
        elevation={0}
        onClick={() => {
          if (!isActive || anchorEl) return
          setDialog(true)
        }}
      >
        <IconButton
          sx={{ position: 'absolute', top: 4, right: 4 }}
          size="small"
          onClick={(e) => {
            e.stopPropagation()
            setAnchorEl(e.currentTarget)
          }}
        >
          <KeyboardArrowDown fontSize="inherit" />
        </IconButton>
        <SdiImage {...thumbnail} />

        <Typography sx={{ mb: 1 }} variant="heading-sm">
          {name}
        </Typography>

        {isActive && (
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, mb: 1 }}>
            {videoFormat && (
              <Typography
                sx={{
                  py: 0.3,
                  px: 0.5,
                  borderRadius: 1,
                  backgroundColor: (theme) =>
                    theme.palette.mode === ThemeTypes.LIGHT ? theme.palette.grey[200] : theme.palette.grey[800],
                }}
                variant="text-sm"
              >
                {videoFormat}
              </Typography>
            )}
            <Typography
              sx={{
                py: 0.3,
                px: 0.5,
                borderRadius: 1,
                backgroundColor: (theme) =>
                  theme.palette.mode === ThemeTypes.LIGHT ? theme.palette.grey[200] : theme.palette.grey[800],
              }}
              variant="text-sm"
            >
              {volume ? `${volume} dBFS` : t('domain:ContentProcessing.Instance.noAudio')}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
          <Typography
            sx={{
              py: 0.3,
              px: 0.5,
              borderRadius: 1,
              backgroundColor: (theme) => getIsActiveColor(isActive, theme.palette.mode),
            }}
            variant="text-sm"
          >
            {t(`domain:ContentProcessing.Instance.${isActive ? 'active' : 'noSignal'}`)}
          </Typography>
        </Box>
        <Menu
          open={!!anchorEl}
          anchorEl={anchorEl}
          anchorReference="anchorEl"
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'bottom',
          }}
          transformOrigin={{
            horizontal: 'center',
            vertical: 'top',
          }}
          onClose={() => setAnchorEl(undefined)}
          MenuListProps={{ disablePadding: true }}
        >
          <CopyToClipboard
            text={id}
            onCopy={() => {
              enqueueSnackbar(t('copiedValueToClipboard', { value: 'ID' }), { variant: 'info' })
              setAnchorEl(undefined)
            }}
          >
            <MenuItem>
              <ListItemIcon>
                <CopyAll fontSize="inherit" />
              </ListItemIcon>
              <ListItemText primary={t('domain:ContentProcessing.Instance.copyId')} />
            </MenuItem>
          </CopyToClipboard>
          <CopyToClipboard
            text={deviceId}
            onCopy={() => {
              enqueueSnackbar(t('copiedValueToClipboard', { value: 'Device ID' }), { variant: 'info' })
              setAnchorEl(undefined)
            }}
          >
            <MenuItem>
              <ListItemIcon>
                <CopyAll fontSize="inherit" />
              </ListItemIcon>
              <ListItemText primary={t('domain:ContentProcessing.Instance.copyDeviceId')} />
            </MenuItem>
          </CopyToClipboard>
        </Menu>
      </Paper>
      <Dialog open={dialog} onClose={() => setDialog(false)}>
        <SdiPanelDialogContent
          onClose={() => setDialog(false)}
          name={name}
          id={id}
          isActive={isActive}
          thumbnail={thumbnail}
          {...sdi}
        />
      </Dialog>
    </>
  )
}

export const SdiPanel = memo(SdiPanelRaw)
